import React from "react";
import { Box, IconButton } from "@mui/material";
import { ArrowDown } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useChatContext } from "./providers/ChatProvider";
import {
  UserBubble,
  AssistantBubble,
  RefusalCard,
  FactsUpdatedPill,
  ProposalCard,
  ConfirmationCard,
  ActionResultPills,
  IngestProgress,
  PlanApprovalCard,
  BudgetPausePrompt,
  ToolApprovalCard,
} from "./MessageBubble";
import { StepTimeline } from "../../chat/components/StepTimeline";
import { BudgetMeter } from "../../chat/components/BudgetMeter";
import { TurnStatus } from "../../chat/hooks/useStreamingTurn";
import { CHAT_COLUMN_MAX_WIDTH, CHAT_COLUMN_GUTTER } from "./layout";
import { TurnMetricsFooter } from "./TurnMetricsFooter";
import { entryVariants, resolveTransition } from "./motion";
import { useStickToBottom } from "./hooks/useStickToBottom";
import { tokens } from "@/theme/tokens";
import type { BoltState } from "@/components/brand/Bolt";
import { computeLiveAvatarState } from "./avatarState";
import type { TimelineEntry } from "../../chat/types";
import { EmptyChatWelcome } from "./EmptyChatWelcome";
export { EmptyChatWelcome }; // keep the existing import surface intact

/** Top-of-history sentinel: auto-loads older messages when scrolled into view
 *  (infinite scroll up), with a click fallback while loading is idle. Prepended
 *  content would normally shove the viewport — Chrome/Firefox scroll-anchor
 *  natively but Safari doesn't — so scroll position is preserved manually by
 *  measuring scrollHeight around the load and offsetting scrollTop. */
function LoadEarlierSentinel({
  scrollRef,
  loadEarlier,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  loadEarlier: () => Promise<void>;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const loadingRef = React.useRef(false);
  const trigger = React.useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    const el = scrollRef.current;
    const beforeHeight = el?.scrollHeight ?? 0;
    const beforeTop = el?.scrollTop ?? 0;
    try {
      await loadEarlier();
    } finally {
      // Keep the message the user was reading in place after the prepend.
      requestAnimationFrame(() => {
        const now = scrollRef.current;
        if (now) now.scrollTop = beforeTop + (now.scrollHeight - beforeHeight);
        loadingRef.current = false;
      });
    }
  }, [loadEarlier, scrollRef]);
  React.useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void trigger();
      },
      { root: scrollRef.current, rootMargin: "200px 0px 0px 0px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [trigger, scrollRef]);
  return (
    <Box ref={ref} sx={{ display: "flex", justifyContent: "center", py: 1 }}>
      <Box
        component="button"
        data-ph="messages-expand_earlier"
        onClick={() => void trigger()}
        sx={{
          border: "none",
          background: "transparent",
          color: "text.secondary",
          fontSize: 13,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Load earlier messages
      </Box>
    </Box>
  );
}

function isUuid(s: string): boolean {
  // Matches the row-id shape we get back from get_history. Rules out the
  // synthetic positional fallback id (sessionId-N) so we don't surface
  // an edit pencil for messages we can't actually target server-side.
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

// The backend prepends attachment notes ("[The user attached N file(s) …]\n\n",
// "[Attached to this turn …]\n\n") to the persisted user text to steer the
// model. They're model instructions, not the user's words — strip them for
// display (chips show the files instead). Keep in step with the note
// builders in unified_agent routes/_attachments.py (_build_note,
// _build_inline_hint, _build_reference_note); the test pins their prefixes.
const ATTACHMENT_NOTES_RE =
  /^(?:\[(?:The user attached \d+ file\(s\)|Attached to this turn)[\s\S]*?\]\n\n)+/;

export function stripAttachmentHint(text: string): string {
  return text.replace(ATTACHMENT_NOTES_RE, "");
}

/** The user turn an assistant message at `idx` answers — the nearest preceding
 *  `user` entry, with the backend's attachment hint stripped so it reads as the
 *  user's own words. Threaded down to the markdown renderer so a table export
 *  can name the question it came from.
 *
 *  Undefined when nothing precedes the answer: a history window paged in from
 *  mid-conversation, or a worker-injected reply. Nothing is fabricated in that
 *  case — an export just omits the line. */
function precedingUserQuestion(
  history: TimelineEntry[],
  idx: number,
): string | undefined {
  for (let i = idx - 1; i >= 0; i--) {
    const m = history[i];
    if (m.role !== "user") continue;
    const text = typeof m.content === "string" ? stripAttachmentHint(m.content).trim() : "";
    return text || undefined;
  }
  return undefined;
}

/** True when the chat has nothing to show yet — no history, idle, no
 *  in-flight assistant parts. Shared by MessageList (centers the welcome)
 *  and ChatPanel (centers the composer with it on the new-chat screen). */
export function useIsEmptyChat(): boolean {
  const { chat } = useChatContext();
  const { history, turn } = chat;
  return (
    history.length === 0 &&
    turn.status === TurnStatus.Idle &&
    turn.assistantParts.length === 0
  );
}

/** `(id) => (...args) => fn(id, ...args)`, returning the same function for the
 *  same id on every render (and always calling the latest `fn`), so a
 *  per-message callback does not defeat a memoized child. */
function usePerIdHandler<A extends unknown[], R>(
  fn: (id: string, ...args: A) => R,
): (id: string) => (...args: A) => R {
  const fnRef = React.useRef(fn);
  fnRef.current = fn;
  const cache = React.useRef(new Map<string, (...args: A) => R>());
  return React.useCallback((id: string) => {
    let handler = cache.current.get(id);
    if (!handler) {
      handler = (...args: A) => fnRef.current(id, ...args);
      cache.current.set(id, handler);
    }
    return handler;
  }, []);
}

/**
 * Renders the chat conversation, active assistant turn, and related interaction states.
 *
 * @param hideWelcome - Whether to suppress the welcome screen when the chat is empty.
 * @returns The chat message list and associated controls.
 */
export function MessageList({ hideWelcome = false }: { hideWelcome?: boolean }) {
  const { chat, registry, features, sessionId, documentsApi } = useChatContext();
  const { history, turn, regenerate, editAndResend, acceptProposal, rejectProposal, confirmAction, declineAction, decidePlan, decideBudget, decideReview, submitFeedback } = chat;
  const isEmpty =
    history.length === 0 &&
    turn.status === TurnStatus.Idle &&
    turn.assistantParts.length === 0;

  // Edit affordance: any user message whose id we can target. Regenerate:
  // only the most recent assistant message on the current branch, and
  // only when the turn isn't actively streaming.
  const lastAssistantIdx = (() => {
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === "assistant") return i;
    }
    return -1;
  })();
  // Only offer regenerate/edit when there's a real, loaded session to target
  // and the just-attempted turn didn't error. A brand-new chat (sessionId
  // null) has nothing persisted server-side, and an errored turn means no
  // usable assistant reply was committed — both guarantee a 404 on
  // regenerate=true / edit_message_id, so we withhold the affordance.
  const canTargetSession = sessionId != null && turn.status !== TurnStatus.Error;
  const canRegenerate =
    features.editRegenerate &&
    canTargetSession &&
    lastAssistantIdx >= 0 &&
    (turn.status === TurnStatus.Idle || turn.status === TurnStatus.Complete);

  // Props for the history bubbles that keep their identity across renders, so
  // AssistantBubble's memo holds while a turn streams below them. Fresh
  // closures and arrays per render re-rendered every history bubble (and each
  // artifact card in it) on every flush (ELASV1-3514).
  const regenerateLast = React.useCallback(() => regenerate(), [regenerate]);
  const feedbackFor = usePerIdHandler(submitFeedback);

  const reduce = useReducedMotion() ?? false;
  const tx = resolveTransition(reduce);

  // Stick-to-bottom: re-pin as content grows while attached. The key folds in
  // every signal that makes the thread taller during a turn (message count,
  // streaming text + thinking length, status) so streaming stays in view.
  const streamingLen =
    turn.assistantParts.reduce(
      (n, p) => n + (p.type === "text" ? p.text.length : 0),
      0,
    ) + (turn.thinkingText?.length ?? 0);
  const contentKey = `${history.length}:${streamingLen}:${turn.status}`;
  const { scrollRef, atBottom, scrollToBottom, onScroll } = useStickToBottom(contentKey);

  // Live turn: the assistant reply is in flight. Rendered as a single
  // AssistantBubble (avatar + thinking + tools + answer) so there's exactly one
  // Bolt avatar for the turn. Reasoning is active while thinking text is
  // streaming and the answer hasn't started (status flips to Streaming on the
  // first answer chunk) — that drives Bolt's thinking expression.
  const turnActive =
    turn.status === TurnStatus.Connecting ||
    turn.status === TurnStatus.Preparing ||
    turn.status === TurnStatus.Streaming;
  const reasoning =
    features.thinking && !!turn.thinkingText && turn.status !== TurnStatus.Streaming;
  const liveToolCalls = [
    ...turn.toolCalls,
    ...(turn.activeTool ? [{ name: turn.activeTool.name, label: turn.activeTool.label }] : []),
  ];
  // Bolt expression for the in-flight bubble. Always-on thinking pose: Bolt
  // looks like it's thinking during the pre-first-token wait even for models
  // that emit no thinking/tool events (see computeLiveAvatarState).
  const liveAvatarState: BoltState = computeLiveAvatarState(turn, features.thinking);

  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
    <Box
      ref={scrollRef}
      onScroll={onScroll}
      sx={{
        flex: 1,
        overflowY: "auto",
        px: CHAT_COLUMN_GUTTER,
        py: { xs: 1, sm: 1.5 },
        // Center the welcome card vertically when the chat is empty;
        // once messages arrive we fall back to top-aligned flow so old
        // turns scroll naturally.
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: isEmpty ? "center" : "flex-start",
        // Subtle top sheen, theme-aware. A heavy white wash muddied the dark
        // surface, so dark mode gets a much fainter highlight (light keeps its).
        background: (t) =>
          t.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0)), transparent"
            : "linear-gradient(180deg, rgba(255,255,255,0.25), rgba(255,255,255,0)), transparent",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: CHAT_COLUMN_MAX_WIDTH,
          display: "flex",
          flexDirection: "column",
          // No list gap: per-bubble asymmetric margins control the rhythm so
          // each user→assistant exchange groups together (see UserBubble /
          // AssistantBubble margins). A gap here would flatten that grouping.
          gap: 0,
        }}
      >
        {isEmpty && !hideWelcome && <EmptyChatWelcome />}
        {chat.historyTruncated && (
          <LoadEarlierSentinel
            scrollRef={scrollRef}
            loadEarlier={chat.loadEarlier}
          />
        )}
        {history.map((m, i) => {
          if (m.role === "proposal") {
            return (
              <ProposalCard
                key={m.id}
                entry={m}
                onAccept={acceptProposal}
                onReject={rejectProposal}
              />
            );
          }
          if (m.role === "user") {
            const rawText = typeof m.content === "string" ? m.content : "";
            // The backend prepends an inline-attachment hint to the persisted
            // user text (steers the model to read the file). Strip it for
            // display — the user sees their words + chips, not the hint.
            const userText = stripAttachmentHint(rawText);
            const hasAttachments = !!(m.attachments && m.attachments.length);
            if (!userText.trim() && !hasAttachments) return null;
            const onEdit =
              features.editRegenerate && isUuid(m.id) && sessionId != null
                ? (newText: string) => editAndResend(m.id, newText)
                : undefined;
            return (
              <UserBubble
                key={m.id}
                text={userText}
                attachments={m.attachments}
                onEdit={onEdit}
                // A mid-turn steer the turn has not read yet. When the turn
                // ended first, the backend holds it for the next one.
                queuedNote={
                  m.queued
                    ? turnActive
                      ? "Queued for this turn"
                      : "Queued — Artie will read this with your next message"
                    : undefined
                }
                // Each chip reads its document to offer "Add to knowledge base"
                // on a chat attachment the caller may write (the promote route
                // re-checks), or to say it is already in the knowledge base.
                attachmentDocuments={hasAttachments ? documentsApi : undefined}
              />
            );
          }
          if (m.role === "assistant") {
            const onRegen =
              canRegenerate && i === lastAssistantIdx ? regenerateLast : undefined;
            const hasContent =
              (typeof m.content === "string" ? m.content.trim() : m.content.length > 0) ||
              !!m.thinking ||
              (m.toolCalls && m.toolCalls.length > 0) ||
              !!(m.workingNotes && m.workingNotes.length > 0) ||
              !!(m.workLog && m.workLog.length > 0);
            if (!hasContent) return null;
            // Feedback only on persisted messages with a real UUID (synthetic
            // positional ids can't be targeted server-side).
            const canFeedback = isUuid(m.id) && sessionId != null;
            return (
              <React.Fragment key={m.id}>
                <AssistantBubble
                  // The bubble takes a string as is; wrapping it here made a
                  // new array per render.
                  parts={m.content}
                  onRegenerate={onRegen}
                  // Blink the most recent assistant avatar so the resting
                  // reply feels alive. While a turn is in flight the live
                  // bubble below is the true "last" one, so stop history here.
                  blink={i === lastAssistantIdx && !turnActive}
                  thinking={features.thinking ? m.thinking : undefined}
                  thinkingMs={m.thinkingMs}
                  toolCalls={m.toolCalls}
                  durationMs={m.durationMs}
                  workingNotes={m.workingNotes}
                  citations={m.citations}
                  workLog={m.workLog}
                  feedback={m.feedback}
                  onFeedback={canFeedback ? feedbackFor(m.id) : undefined}
                  /* Metrics now render inline on the bubble's action footer,
                     next to copy/feedback/regenerate (no separate footer). */
                  metrics={m.metrics ?? null}
                  defaultCollapsed={features.thinkingCollapsed}
                  // Provenance for a table export: the question this answers.
                  userQuestion={precedingUserQuestion(history, i)}
                />
              </React.Fragment>
            );
          }
          return null;
        })}

        {/* The whole in-flight assistant turn (avatar + thinking + tools +
            streaming answer) renders as one AssistantBubble. On completion this
            unmounts and the promoted history bubble takes over — so there's
            never a duplicate avatar or a duplicate thinking disclosure.
            Rendered for the WHOLE active turn (not just once content arrives) so
            the Bolt shows its thinking pose during the pre-first-token wait —
            even for models that emit no thinking/tool events. WorkTimeline
            self-hides while empty, so the wait renders as just the thinking
            Bolt with no empty disclosure. */}
        {turnActive && (
            <AssistantBubble
              isLive
              // Bolt expression: thinking during the wait / real reasoning;
              // talking once the answer streams; idle otherwise
              // (see computeLiveAvatarState).
              avatarState={liveAvatarState}
              // Blink the live avatar only when it's at rest (idle) — the
              // thinking/talking poses already carry their own motion.
              blink={liveAvatarState === "idle"}
              parts={turn.assistantParts}
              thinking={features.thinking ? turn.thinkingText : undefined}
              thinkingStreaming={reasoning}
              thinkingMs={turn.thinkingMs}
              toolCalls={liveToolCalls}
              workingNotes={turn.workingNotes}
              citations={turn.citations}
              workLog={turn.workLog}
              defaultCollapsed={features.thinkingCollapsed}
            />
          )}

        {(turn.status !== TurnStatus.Complete || turn.budgetPause) &&
          turn.steps.length > 0 && <StepTimeline steps={turn.steps} />}
        {(turn.status !== TurnStatus.Complete || turn.budgetPause) && (
          <BudgetMeter budget={turn.budget} />
        )}

        {/* Mutating-tool outcome pills ("✓ Renamed role") — the explicit
            "did my write land?" signal. Shown for the live turn and kept
            visible after it completes. */}
        {turn.actionResults.length > 0 && (
          <ActionResultPills results={turn.actionResults} />
        )}

        {/* KB ingestion progress for files uploaded / ingested this turn. */}
        {turn.ingests.length > 0 && <IngestProgress ingests={turn.ingests} />}

        <AnimatePresence>
          {turn.pendingConfirmation && (
            <motion.div key="confirm" variants={entryVariants} initial={reduce ? false : "hidden"} animate="visible" exit="hidden" transition={tx}>
              <ConfirmationCard
                confirmation={turn.pendingConfirmation}
                onApprove={confirmAction}
                onCancel={declineAction}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {turn.pendingPlan && (
            <motion.div key="plan" variants={entryVariants} initial={reduce ? false : "hidden"} animate="visible" exit="hidden" transition={tx}>
              <PlanApprovalCard
                todos={turn.pendingPlan.todos}
                onApprove={() => decidePlan("approve")}
                onReject={() => decidePlan("reject")}
                onEdit={(todos) => decidePlan("edit", { edited_todos: todos })}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tool approval gate: the turn ENDED holding a gated call and nothing
            runs until the user decides (spec §5.2). Each gated tool has its
            own card (send_email, promote_to_knowledge) — gating another needs
            one too, or it renders an email-shaped frame around it. */}
        <AnimatePresence>
          {turn.pendingReview && (
            <motion.div key="review" variants={entryVariants} initial={reduce ? false : "hidden"} animate="visible" exit="hidden" transition={tx}>
              <ToolApprovalCard review={turn.pendingReview} onDecide={decideReview} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {turn.budgetPause && (
            <motion.div key="budget-pause" variants={entryVariants} initial={reduce ? false : "hidden"} animate="visible" exit="hidden" transition={tx}>
              <BudgetPausePrompt pause={turn.budgetPause} onDecide={decideBudget} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {turn.status === TurnStatus.Complete && turn.metrics && (
            <motion.div key="metrics" variants={entryVariants} initial={reduce ? false : "hidden"} animate="visible" exit="hidden" transition={tx}>
              <TurnMetricsFooter metrics={turn.metrics} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Refused turns get a structured card; only fall through to the
            generic error banner when there's an `error` payload AND no
            refusal — that combination means the system failed, not the
            assistant declined. */}
        {turn.refused && <RefusalCard text={turn.refusalText} />}
        {turn.status === TurnStatus.Error && turn.error && !turn.refused && (
          <Box sx={{ color: "error.main", px: 1, py: 0.5 }}>Error: {turn.error}</Box>
        )}

        <FactsUpdatedPill triggerKey={turn.factsUpdatedAt} />

        {/* Tier 3 #11 hook: future frame kinds go through registry.
            v1 has no registered kinds. */}
        {void registry}
      </Box>
    </Box>
      {/* Floating "scroll to bottom" — appears only when the user has scrolled
          up and there's something below. Sits over the scroll viewport. */}
      <AnimatePresence>
        {!atBottom && !isEmpty && (
          <motion.div
            key="scroll-to-bottom"
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: 8 }}
            transition={tx}
            style={{ position: "absolute", bottom: 16, left: 0, right: 0, display: "flex", justifyContent: "center", pointerEvents: "none" }}
          >
            <IconButton
              aria-label="scroll to latest message"
              data-ph="messages-open_latest"
              onClick={() => scrollToBottom()}
              sx={(t) => {
                const dark = t.palette.mode === "dark";
                const ring = dark ? "rgba(255,255,255,0.09)" : "rgba(16,24,40,0.08)";
                return {
                  pointerEvents: "auto",
                  // Flat disc floating over the conversation — a real
                  // surface tone + ring so it stays visible in dark mode,
                  // where matching the page colour made it disappear.
                  bgcolor: "background.paper",
                  border: `1px solid ${ring}`,
                  color: "text.secondary",
                  boxShadow: tokens.elevation[1],
                  width: 38,
                  height: 38,
                  "&:hover": { color: "text.primary", boxShadow: tokens.elevation[2] },
                  "&:active": { boxShadow: tokens.elevation[1] },
                };
              }}
            >
              <ArrowDown size={18} />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
